import React, { useState, useEffect } from 'react';
import { getArtMovementByName } from '../services/artMovementService';

const ArtMovementPopup = ({ artMovement, isOpen, onClose }) => {
  const [movementData, setMovementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && artMovement) {
      fetchArtMovementData();
    }
  }, [isOpen, artMovement]);

  const fetchArtMovementData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getArtMovementByName(artMovement);
      
      if (data) {
        setMovementData(data);
      } else {
        setError('Art movement information not found');
      }
    } catch (err) {
      console.error('Error fetching art movement data:', err);
      setError('Failed to load art movement information');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10002] p-4">
      <div className="relative w-full max-w-4xl max-h-[80vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-3 bg-white hover:bg-gray-50 rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
          title="Close"
        >
          <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-amber-200 to-amber-100 p-6 border-b-4 border-amber-300">
          <h2 className="text-2xl font-bold text-amber-800">{artMovement}</h2>
          <p className="text-amber-700 mt-1">Art Movement Overview</p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              <span className="ml-3 text-gray-600">Loading art movement information...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {movementData && !loading && (
            <div className="space-y-4">
              {movementData.description && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{movementData.description}</p>
                </div>
              )}

              {movementData.characteristics && movementData.characteristics.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Key Characteristics</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    {movementData.characteristics.map((characteristic, index) => (
                      <li key={index}>{characteristic}</li>
                    ))}
                  </ul>
                </div>
              )}

              {movementData.timePeriod && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Time Period</h3>
                  <p className="text-gray-700">{movementData.timePeriod}</p>
                </div>
              )}

              {movementData.famousArtists && movementData.famousArtists.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Famous Artists</h3>
                  <p className="text-gray-700">{movementData.famousArtists.join(', ')}</p>
                </div>
              )}

              {movementData.techniques && movementData.techniques.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Common Techniques</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    {movementData.techniques.map((technique, index) => (
                      <li key={index}>{technique}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!movementData && !loading && !error && (
            <div className="text-center py-8">
              <p className="text-gray-500">No information available for this art movement.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtMovementPopup;
